import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { optionalColumns } from '../../staticData';
import { Button, SequenceTable, SearchBar } from '../../atomicComponents';
import LeftArrowIcon from '@material-ui/icons/KeyboardArrowLeft';
import RightArrowIcon from '@material-ui/icons/KeyboardArrowRight';
import styles from './ResequenceTransferList.module.css';
import ResequencingToolbar from '../ResequencingToolbar/ResequencingToolbar';

const ResequenceTransferList = ({
  date,
  sourceData,
  lineName,
  handlePreview,
  handleCancel,
  transferListDraft,
}) => {
  const [sourceListData, setSourceListData] = useState([]);
  const [destinationListData, setDestinationListData] = useState([]);

  const [vcSearch, setVcSearch] = useState('');
  const [quantitySearch, setQuantitySearch] = useState('');

  const getFilteredSourceList = () => {
    let filtered = sourceListData;
    if (vcSearch) {
      filtered = filtered.filter(row =>
        row.vc?.toString().toLowerCase().includes(vcSearch.toLowerCase()),
      );
    }
    if (quantitySearch) {
      const qty = parseInt(quantitySearch);
      if (!isNaN(qty) && qty > 0) {
        filtered = filtered.slice(0, qty);
      }
    }
    return filtered;
  };

  const filteredSourceListData = getFilteredSourceList();

  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);

  useEffect(() => {
    if (transferListDraft?.length) {
      setDestinationListData([...transferListDraft]);
      setSourceListData([]);
    } else {
      setSourceListData([...sourceData]);
      setDestinationListData([]);
    }
  }, [transferListDraft, sourceData]);

  const handleRowSelectForListCurry = setListData => id => {
    setListData(prevState =>
      prevState.map(row =>
        row.id !== id ? row : { ...row, isChecked: !row.isChecked },
      ),
    );
  };

  const handleAllRowsSelectForListCurry = setListData => idSet => {
    setListData(prevState => {
      let cumulativeCheckState = prevState
        .filter(row => idSet.has(row.id))
        .every(row => row.isChecked);

      return prevState.map(row =>
        idSet.has(row.id)
          ? {
            ...row,
            isChecked: !cumulativeCheckState,
          }
          : row,
      );
    });
  };

  const moveSelectedIntoDestination = () => {
    const checkRemovedSelectedRows = sourceListData
      .filter(row => row.isChecked)
      .map(({ isChecked, ...rest }) => ({ ...rest }));

    setSourceListData(prev => prev.filter(row => !row.isChecked));
    setDestinationListData(prev => [...prev, ...checkRemovedSelectedRows]);
  };

  const moveSelectedIntoSource = () => {
    const checkRemovedSelectedRows = destinationListData
      .filter(row => row.isChecked)
      .map(({ isChecked, ...rest }) => ({ ...rest }));

    setDestinationListData(prev => prev.filter(row => !row.isChecked));
    setSourceListData(prev => [...prev, ...checkRemovedSelectedRows]);
  };

  return (
    <>
      <div style={{ height: '8%' }}>
        <ResequencingToolbar
          selectedOptionalColNames={selectedOptionalColNames}
          handleColumnSelection={selection =>
            setSelectedOptionalColNames(selection)
          }
          lineName={lineName}
          date={date}
          optionalColumns={optionalColumns}
          disableSubmit={sourceListData.length}
          handleSubmit={() => handlePreview(destinationListData)}
          submitButtonText={'Preview'}
          handleCancel={handleCancel}
          pendingCount={sourceListData.length}
          isDragAndDropEnabled={false}
        />
      </div>
      <div className={styles.searchContainer}>
        <div className={styles.searchItem}>
          <SearchBar
            value={vcSearch}
            onChange={setVcSearch}
            placeholder="Search VC"
          />
        </div>
        <div className={styles.searchItem}>
          <SearchBar
            value={quantitySearch}
            onChange={setQuantitySearch}
            placeholder="Quantity"
          />
        </div>
      </div>
      <div className={styles.tablesArea}>
        <div className={styles.tableContainer}>
          <SequenceTable
            showRowNumbers
            enableRowsSelection
            sourceRows={filteredSourceListData}
            selectedOptionalColNames={selectedOptionalColNames}
            handleRowSelect={handleRowSelectForListCurry(setSourceListData)}
            maxEmptyRowPadding={0}
            rowHeight={36}
            headerRowHeight={38}
            handleAllRowsSelect={handleAllRowsSelectForListCurry(
              setSourceListData,
            )}
            rowColors={[null, null]}
            classNames={{
              tableRowClassName: ({ isChecked }, index) => {
                if (isChecked) {
                  return index % 2 === 0
                    ? styles.checkedRowEven
                    : styles.checkedRowOdd;
                }
                return index % 2 === 0 ? styles.lftRowEven : styles.lftRowOdd;
              },
            }}
          />
        </div>
        <div className={styles.transferButtonsContainer}>
          <Button
            variant="primary"
            className={styles.transferButton}
            disabled={sourceListData.every(row => !row.isChecked)}
            onClick={moveSelectedIntoDestination}
          >
            <RightArrowIcon />
          </Button>
          <Button
            variant="primary"
            className={styles.transferButton}
            disabled={destinationListData.every(row => !row.isChecked)}
            onClick={moveSelectedIntoSource}
          >
            <LeftArrowIcon />
          </Button>
        </div>
        <div className={styles.tableContainer}>
          <SequenceTable
            showRowNumbers
            enableRowsSelection
            sourceRows={destinationListData}
            selectedOptionalColNames={selectedOptionalColNames}
            handleRowSelect={handleRowSelectForListCurry(
              setDestinationListData,
            )}
            maxEmptyRowPadding={0}
            rowHeight={36}
            headerRowHeight={38}
            classNames={{
              tableContainerClassName: styles.rightTableContainer,
              tableHeadClassName: styles.rightTableHead,
              tableRowClassName: ({ isChecked }, index) => {
                if (isChecked) {
                  return index % 2 === 0
                    ? styles.checkedRowEven
                    : styles.checkedRowOdd;
                }
                return index % 2 === 0 ? styles.rgtRowEven : styles.rgtRowOdd;
              },
            }}
            handleAllRowsSelect={handleAllRowsSelectForListCurry(
              setDestinationListData,
            )}
            rowColors={[null, null]}
            renderPlaceholder={() => (
              <div className={styles.placeholderContainer}>
                <Button
                  variant="primary"
                  className={styles.placeholderButton}
                  disabled={sourceListData.every(row => !row.isChecked)}
                  onClick={moveSelectedIntoDestination}
                >
                  <RightArrowIcon />
                </Button>
                <p>
                  To create your own sequence please select the VC from left and
                  use arrow to move to the right
                </p>
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
};

ResequenceTransferList.propTypes = {
  date: PropTypes.objectOf(moment),
  sourceData: PropTypes.arrayOf(
    PropTypes.shape({
      vc: PropTypes.any.isRequired,
      description: PropTypes.any,
      serialNumber: PropTypes.number.isRequired,
      id: PropTypes.any.isRequired,
    }),
  ),
  transferListDraft: PropTypes.arrayOf(
    PropTypes.shape({
      vc: PropTypes.any.isRequired,
      description: PropTypes.any,
      serialNumber: PropTypes.number.isRequired,
      id: PropTypes.any.isRequired,
    }),
  ),
  lineName: PropTypes.string.isRequired,
  handleCancel: PropTypes.func.isRequired,
  handlePreview: PropTypes.func.isRequired,
};

export default ResequenceTransferList;
